import { useState } from 'react';
import { useConfig } from '../lib/ConfigContext';
import { ExternalLink } from 'lucide-react';

interface Props {
  address?: string | null;
  /** Compact (no image) when true — used in the small card grid. Defaults to full image. */
  compact?: boolean;
}

// Address card with Google Street View thumbnail. Click → opens full Street View in a new tab.
// Falls back to text-only when no Maps key is configured or when the image fails to load
// (e.g. Street View Static API not enabled, or no imagery for the address).
export function AddressCard({ address, compact = false }: Props) {
  const config = useConfig();
  const [imgFailed, setImgFailed] = useState(false);

  if (!address) {
    return <span className="text-slate-600">—</span>;
  }

  if (compact || !config.googleMapsApiKey || imgFailed) {
    return (
      <a
        href={mapLink(address)}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-ink hover:text-slate-500 inline-flex items-center gap-1.5"
      >
        {address}
        <ExternalLink size={11} className="opacity-60" />
      </a>
    );
  }

  // 600×220 fits the detail card column at 2x for retina. Address goes through the API as the
  // location query — Google geocodes it and finds the nearest panorama.
  const imgUrl =
    `https://maps.googleapis.com/maps/api/streetview?size=600x220` +
    `&location=${encodeURIComponent(address)}` +
    `&key=${config.googleMapsApiKey}`;

  return (
    <a
      href={mapLink(address)}
      target="_blank"
      rel="noopener noreferrer"
      className="block group"
      title="Open in Google Street View"
    >
      <div className="text-sm text-brand-ink mb-2 inline-flex items-center gap-1.5 group-hover:text-slate-500">
        {address}
        <ExternalLink size={11} className="opacity-60" />
      </div>
      <img
        src={imgUrl}
        alt={`Street View of ${address}`}
        className="w-full h-auto rounded-lg border border-slate-200 group-hover:border-slate-300 transition"
        loading="lazy"
        onError={() => setImgFailed(true)}
      />
    </a>
  );
}

function mapLink(address: string): string {
  // `map_action=pano` opens Google Maps directly into Street View. If no panorama exists at
  // that address, Google falls back to the regular map view automatically.
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${encodeURIComponent(address)}&query=${encodeURIComponent(address)}`;
}
